import Button from 'components/Button'
import FormField, { Label } from 'components/FormField'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import * as yup from 'yup'
import { ApolloError, useApolloClient } from '@apollo/client'
import ServerErrorMessage from 'components/ServerErrorMessage'
import * as Validation from 'utils/formFieldValidations'
import PhoneNumberField from 'formFields/PhoneNumberField'
import SpaceForKeyboard from 'components/SpaceForKeyboard'

export type InitialValues = {
  firstName?: string
  lastName?: string
  username?: string
  email?: string
  phoneNumber?: string | null
}

interface Props {
  buttonText: string
  onSubmit: (values: InitialValues) => void
  state: {
    loading?: boolean
    error?: ApolloError | undefined
  }
  initialValues: InitialValues
  canEditEmail?: boolean
  canEditUsername?: boolean
}

const FormPerson = ({
  buttonText,
  onSubmit,
  state,
  initialValues,
  canEditEmail,
  canEditUsername,
}: Props) => {
  const client = useApolloClient()
  const validationSchema = yup.object().shape({
    firstName: Validation.firstName,
    lastName: Validation.lastName,
    phoneNumber: Validation.uniquePhoneNumber(
      client,
      initialValues.phoneNumber,
    ),
    ...(canEditUsername
      ? {
          username: Validation.username(client, initialValues.username),
        }
      : undefined),
    ...(canEditEmail
      ? {
          email: Validation.uniqueEmail(client, initialValues.email),
        }
      : undefined),
  })
  const formMethods = useForm({
    defaultValues: {
      firstName: initialValues.firstName || '',
      lastName: initialValues.lastName || '',
      username: initialValues.username || '',
      email: initialValues.email || '',
      phoneNumber: initialValues.phoneNumber || '',
    },
    resolver: yupResolver(validationSchema),
  })
  const { register, handleSubmit, errors, control } = formMethods

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* First Name */}
        <div className="py-2">
          <FormField
            type="text"
            label="First Name"
            name="firstName"
            register={register}
            error={errors?.firstName?.message}
          />
        </div>

        {/* Last Name */}
        <div className="py-2">
          <FormField
            type="text"
            label="Last Name"
            name="lastName"
            register={register}
            error={errors?.lastName?.message}
          />
        </div>

        {/* Username */}
        {canEditUsername && (
          <div className="py-2">
            <FormField
              type="text"
              label="Username"
              name="username"
              register={register}
              error={errors?.username?.message}
            />
          </div>
        )}

        {/* Email */}
        <div className="py-2">
          {canEditEmail ? (
            <FormField
              type="text"
              label="Email"
              name="email"
              register={register}
              error={errors?.email?.message}
              disabled={!canEditEmail}
            />
          ) : (
            <>
              <Label>Email</Label>
              <div>{initialValues.email || 'N/A'}</div>
            </>
          )}
        </div>

        {/* Phone Number */}
        <div className="py-2">
          <PhoneNumberField
            label="Phone Number"
            name="phoneNumber"
            control={control}
          />
        </div>

        {state.error && (
          <ServerErrorMessage>{state.error.message}</ServerErrorMessage>
        )}

        {/* CREATE */}
        <div className="pt-6">
          <Button type="submit" $type="primary" $fluid loading={state.loading}>
            {buttonText}
          </Button>
        </div>

        <SpaceForKeyboard $size="lg" />
      </form>
    </FormProvider>
  )
}

FormPerson.defaultProps = {
  buttonText: 'Submit',
  canEditPicture: true,
  canEditEmail: true,
}

export default FormPerson
